import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from "../../Constants/constants";
import Loading from "../Loading/Loading";

const initialAuthState = {
    isLoggedIn: false,
    userRole: "",
    authError: null,
    userDetails: {
        id: null,
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        // normalized role fields
        role: null,       // string role name e.g. "ICT"
        role_id: null,    // number e.g. 2
        role_obj: null,   // full object from backend
    },
    loading: true,
    currentUser: null,
};

const AuthContext = createContext({
    ...initialAuthState,
    login: async () => {},
    logout: () => {},
    resetAuthError: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [state, setState] = useState(initialAuthState);

    // ✅ useRef so the timer persists reliably across renders
    const logoutTimerRef = useRef(null);
    const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes

    const resetInactivityTimer = () => {
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = setTimeout(() => logout(), INACTIVITY_TIMEOUT);
    };

    useEffect(() => {
        const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
        const onActivity = () => resetInactivityTimer();

        events.forEach(event => window.addEventListener(event, onActivity));
        resetInactivityTimer();

        return () => {
            events.forEach(event => window.removeEventListener(event, onActivity));
            if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        };
    }, []);

    // NOTE: keeping your behavior: logout on refresh
    useEffect(() => {
        const handleBeforeUnload = () => {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('userRole');
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    const resetAuthError = () => {
        setState(prev => ({ ...prev, authError: null }));
    };

    const logout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userRole');
        setState({ ...initialAuthState, loading: false });
        console.log("User logged out due to inactivity, session expiration, or page refresh.");
    };

    const handleError = (error) => {
        let errorMessage = "An unexpected error occurred. Please try again later.";

        if (error.response) {
            switch (error.response.status) {
                case 401:
                    errorMessage = "Login failed. Invalid username or password.";
                    logout();
                    break;
                case 403:
                    errorMessage = "Your account is not approved yet. Please contact admin.";
                    break;
                case 404:
                    errorMessage = "Login service is unavailable.";
                    break;
                default:
                    errorMessage = "Wrong Username or Password.";
            }
        } else if (error.request) {
            errorMessage = "No response from the server. Please check your network connection or try again later.";
        } else {
            errorMessage = `Error: ${error.message}`;
        }

        setState(prev => ({ ...prev, authError: errorMessage, loading: false }));
    };

    // ✅ IMPORTANT: normalize role so UI can safely use currentUser.role (string)
    const normalizeUserFromMe = (data) => {
        const roleObj = data?.role ?? null; // backend sends object in your log
        const roleName =
            typeof roleObj === 'string'
                ? roleObj
                : (roleObj?.name ?? null);

        const roleId =
            typeof roleObj === 'object' && roleObj?.id != null
                ? roleObj.id
                : (data?.role_id ?? null);

        return {
            id: data?.id ?? null,
            firstName: data?.first_name ?? '',
            lastName: data?.last_name ?? '',
            email: data?.email ?? '',
            username: data?.username ?? '',
            role: roleName,
            role_id: roleId,
            role_obj: roleObj,
        };
    };

    const fetchUserDetails = async (token) => {
        try {
            const response = await axios.get(`${BASE_URL}/users/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            console.log('[AuthContext] /users/me response.data:', response.data);

            return normalizeUserFromMe(response.data);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                logout();
            } else {
                console.error("Error fetching user details:", error);
            }
            throw error;
        }
    };

    const login = async (username, password) => {
        setState(prev => ({ ...prev, loading: true }));

        try {
            const payload = { username: username.toLowerCase().trim(), password };

            const response = await axios.post(`${BASE_URL}/login/`, payload, {
                headers: { 'Content-Type': 'application/json' },
                timeout: 10000,
            });

            console.log('[AuthContext] /login response.data:', response.data);

            if (response.data.access_token && response.data.is_active) {
                localStorage.setItem('accessToken', response.data.access_token);
                localStorage.setItem('userRole', response.data.role);

                const userDetails = await fetchUserDetails(response.data.access_token);

                // ✅ if /login role exists, use it as a fallback (but /users/me is now primary)
                const mergedUser = {
                    ...userDetails,
                    role: userDetails.role ?? response.data.role ?? null,
                };

                setState({
                    isLoggedIn: true,
                    userRole: mergedUser.role || response.data.role || "",
                    authError: null,
                    userDetails: mergedUser,
                    currentUser: mergedUser,
                    loading: false,
                });
            } else {
                setState(prev => ({
                    ...prev,
                    authError: "Your account is not approved yet. Please contact admin.",
                    loading: false,
                }));
            }
        } catch (error) {
            handleError(error);
        }
    };

    const initializeAuth = async () => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            setState(prev => ({ ...prev, loading: false }));
            return;
        }

        try {
            const userDetails = await fetchUserDetails(token);
            const storedRole = localStorage.getItem('userRole');

            const mergedUser = {
                ...userDetails,
                role: userDetails.role ?? storedRole ?? null,
            };

            setState(prev => ({
                ...prev,
                isLoggedIn: true,
                userRole: mergedUser.role || storedRole || "",
                userDetails: mergedUser,
                currentUser: mergedUser,
                loading: false,
            }));
        } catch (error) {
            console.error('Error fetching user details:', error);
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    useEffect(() => {
        initializeAuth();
    }, []);

    if (state.loading) return <Loading />;

    return (
        <AuthContext.Provider value={{ ...state, login, logout, resetAuthError }}>
            {children}
        </AuthContext.Provider>
    );
};
